import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { runningPath, normalizeSoccerRedirect, accountPath } from '../utils/paths';
import { normalizeClubSport } from '../config/clubSports';
import {
  RUNNER_EXPERIENCE_LEVELS,
  RUNNER_WEEKLY_MILEAGE,
  RUNNER_PREFERRED_DISTANCES,
  RUNNER_GROUP_PREFERENCES,
} from '../config/runnerProfileOptions';
import './PlayerRegistration.css';
import './RunningProfile.css';
import './AccountProfile.css';

const emptyForm = {
  experienceLevel: 'BEGINNER',
  typicalPaceMinPerMile: '',
  weeklyMileage: '',
  groupPreference: '',
  preferredDistances: [],
  injuryNotes: '',
};

const RunningProfileRegistration = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const [account, setAccount] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [requestClubId, setRequestClubId] = useState('');
  const [runningClubs, setRunningClubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      const clubId = searchParams.get('clubId') || sessionStorage.getItem('votingClubId');
      const registerPath = clubId ? `/register?clubId=${encodeURIComponent(clubId)}` : '/register';
      navigate(registerPath, { replace: true });
    }
  }, [isAuthenticated, navigate, searchParams]);

  useEffect(() => {
    if (!user?.id) return;
    api.getAccountProfile(user.id).then(setAccount).catch(() => setAccount(null));
  }, [user?.id]);

  useEffect(() => {
    const preselectedClub = searchParams.get('clubId') || sessionStorage.getItem('votingClubId');
    if (preselectedClub) setRequestClubId(preselectedClub);
  }, [searchParams]);

  useEffect(() => {
    const loadClubs = async () => {
      try {
        const data = await api.getApprovedClubs();
        setRunningClubs(
          (Array.isArray(data) ? data : []).filter(
            (club) => normalizeClubSport(club.sport) === 'RUNNING'
          )
        );
      } catch {
        setRunningClubs([]);
      }
    };
    loadClubs();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
  };

  const toggleDistance = (distance) => {
    setFormData((prev) => {
      const selected = prev.preferredDistances.includes(distance)
        ? prev.preferredDistances.filter((d) => d !== distance)
        : [...prev.preferredDistances, distance];
      return { ...prev, preferredDistances: selected };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        experienceLevel: formData.experienceLevel,
        typicalPaceMinPerMile: formData.typicalPaceMinPerMile
          ? parseFloat(formData.typicalPaceMinPerMile, 10)
          : null,
        weeklyMileage: formData.weeklyMileage || null,
        groupPreference: formData.groupPreference || null,
        preferredDistances: formData.preferredDistances,
        injuryNotes: formData.injuryNotes || null,
      };
      const response = await api.saveRunnerProfile(user.id, payload, { create: true });
      let successMessage = 'Running profile saved.';

      const clubIdToJoin = requestClubId || sessionStorage.getItem('votingClubId');
      const membershipPlayerId = response.membershipPlayerId;
      if (clubIdToJoin && membershipPlayerId) {
        try {
          await api.requestClubMembership(clubIdToJoin, membershipPlayerId);
          successMessage += ' Your request to join the running club was sent.';
        } catch (clubErr) {
          if (clubErr.message?.toLowerCase().includes('already') || clubErr.message?.toLowerCase().includes('pending')) {
            successMessage += ` ${clubErr.message}`;
          }
        }
      }

      setSuccess(successMessage);
      const redirectUrl = sessionStorage.getItem('votingRedirectUrl');
      setTimeout(() => {
        if (redirectUrl) {
          sessionStorage.removeItem('votingRedirectUrl');
          sessionStorage.removeItem('votingClubId');
          navigate(normalizeSoccerRedirect(redirectUrl), { replace: true });
        } else {
          navigate(runningPath('/profile'), { replace: true });
        }
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to save running profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card running-profile-page">
      <h2>Complete your running profile</h2>
      <p className="form-help" style={{ marginBottom: '1rem' }}>
        Add running-specific details below. Your name, photo, and contact info come from your{' '}
        <Link to={accountPath()}>account profile</Link> and apply across all sports.
      </p>

      {account && (
        <p className="account-profile-page__shared-note">
          Signed in as <strong>{account.name}</strong>
          {account.phoneNumber ? ` · ${account.phoneNumber}` : ''}.{' '}
          <Link to={accountPath()}>Update account details →</Link>
        </p>
      )}

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="experienceLevel">Running experience *</label>
          <select
            id="experienceLevel"
            name="experienceLevel"
            value={formData.experienceLevel}
            onChange={handleChange}
            required
          >
            {RUNNER_EXPERIENCE_LEVELS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="typicalPaceMinPerMile">Typical easy pace (min/mile, optional)</label>
          <input
            type="number"
            id="typicalPaceMinPerMile"
            name="typicalPaceMinPerMile"
            min="4"
            max="20"
            step="0.1"
            value={formData.typicalPaceMinPerMile}
            onChange={handleChange}
            placeholder="e.g. 9.5 for 9:30 / mile"
          />
        </div>

        <div className="form-group">
          <label htmlFor="weeklyMileage">Weekly mileage (optional)</label>
          <select
            id="weeklyMileage"
            name="weeklyMileage"
            value={formData.weeklyMileage}
            onChange={handleChange}
          >
            <option value="">— Select —</option>
            {RUNNER_WEEKLY_MILEAGE.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Preferred distances (optional)</label>
          <div className="running-profile-page__chips">
            {RUNNER_PREFERRED_DISTANCES.map((opt) => {
              const selected = formData.preferredDistances.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={`running-profile-page__chip${selected ? ' is-selected' : ''}`}
                  onClick={() => toggleDistance(opt.value)}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="groupPreference">Group run preference (optional)</label>
          <select
            id="groupPreference"
            name="groupPreference"
            value={formData.groupPreference}
            onChange={handleChange}
          >
            <option value="">— Select —</option>
            {RUNNER_GROUP_PREFERENCES.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="injuryNotes">Injury or medical notes (optional, private)</label>
          <textarea
            id="injuryNotes"
            name="injuryNotes"
            value={formData.injuryNotes}
            onChange={handleChange}
            rows={3}
            maxLength={500}
          />
        </div>

        <div className="form-group">
          <label htmlFor="requestClubId">Request to join a running club (optional)</label>
          <select
            id="requestClubId"
            value={requestClubId}
            onChange={(e) => setRequestClubId(e.target.value)}
            style={{ width: '100%', padding: '0.5rem' }}
          >
            <option value="">— No club / I'll join later —</option>
            {runningClubs
              .filter((club) => club.allowNewMembers !== false)
              .map((club) => (
                <option key={club.id} value={club.id}>
                  {club.name}
                </option>
              ))}
          </select>
        </div>

        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Saving...' : 'Save running profile'}
        </button>
      </form>
    </div>
  );
};

export default RunningProfileRegistration;
